// Tarjeta "Bebidas" de la ficha del presupuesto (formato nuevo).
//
// Arriba, el modo de bebidas del evento: variado sin alcohol (por defecto),
// variado con alcohol o personalizado. Elegir un modo lo guarda y lo deja
// validado (quién y cuándo). Mientras no se valida, aviso "Pendiente de validar"
// con el mismo estilo que la pestaña Material.
//
// Debajo:
// - Variado: las bebidas que salen en Compras y en la hoja de material
//   (`bebidas_variado`, calculadas por el backend).
// - Personalizado: el bloque de bebidas por menú de siempre (`children`).
//
// El estado lo carga la ficha (lo usa también "Por hacer"); aquí solo se
// muestra y se cambia el modo.
//
// Si ningún menú lleva el plato de bebida mixto (`estado.aplica === false`),
// no hay modo que elegir: solo una línea explicativa y el bloque de bebidas
// por menú (`children`). Sin `aplica` (backend antiguo), como siempre.
//
// Barra libre del lunch (`estado.cubiertas_barra_libre`): la bebida del lunch
// de esas personas no se cuenta. Se avisa arriba; si cubre todo el lunch, el
// modo no aplica y el aviso sustituye a la explicación del ramal "no aplica".
import React, { useState } from 'react';
import { presupuestosAPI } from '../../api/api';

const MODOS_POR_DEFECTO = [
  { valor: 'sin_alcohol', texto: 'Variado sin alcohol' },
  { valor: 'con_alcohol', texto: 'Variado con alcohol' },
  { valor: 'personalizado', texto: 'Personalizado' },
];

const AYUDA_MODO = {
  sin_alcohol: 'Reparto estándar sin cerveza ni alcohol; su parte se reparte entre el resto',
  con_alcohol: 'Reparto estándar con cerveza',
  personalizado: 'Eliges las bebidas de cada menú',
};

const claseTh = 'px-3 py-2 text-xs font-bold tracking-wide uppercase text-[#5B6B64] border-b border-[#E1E6E3]';
const claseTd = 'px-3 py-2 text-sm border-b border-[#EEF1EF]';

// dd/mm HH:MM (las fechas del backend llegan en UTC, a veces sin zona)
const formatFechaHora = (valor) => {
  if (!valor) return '';
  const texto = String(valor);
  const f = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`);
  if (Number.isNaN(f.getTime())) return '';
  const dd = String(f.getDate()).padStart(2, '0');
  const mm = String(f.getMonth() + 1).padStart(2, '0');
  const hh = String(f.getHours()).padStart(2, '0');
  const mi = String(f.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} ${hh}:${mi}`;
};

const BebidasModo = ({ presupuestoId, estado, cargando = false, onCambio, onEstadoGuardado, children }) => {
  const [guardando, setGuardando] = useState(null); // modo que se está guardando
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);

  const modo = estado?.modo || 'sin_alcohol';
  const validado = !!estado?.validado_en;
  const modos = (Array.isArray(estado?.modos) && estado.modos.length > 0) ? estado.modos : MODOS_POR_DEFECTO;
  const variado = Array.isArray(estado?.bebidas_variado) ? estado.bebidas_variado : [];
  const cubiertas = Number(estado?.cubiertas_barra_libre) || 0;
  const textoCubiertas = `Bebida del lunch cubierta por la barra libre: ${cubiertas} ${cubiertas === 1 ? 'persona' : 'personas'}. Compras y la hoja de material no la cuentan.`;

  const elegirModo = async (valor) => {
    if (!presupuestoId || guardando) return;
    setGuardando(valor);
    setError(null);
    if (onEstadoGuardado) onEstadoGuardado('inicio');
    try {
      const { data } = await presupuestosAPI.setBebidasModo(presupuestoId, valor);
      setAviso(data?.aviso || null);
      if (onCambio) onCambio(data);
      if (onEstadoGuardado) onEstadoGuardado('ok');
    } catch (err) {
      setError(`No se pudo guardar el modo de bebidas: ${err?.response?.data?.detail || err.message}`);
      if (onEstadoGuardado) onEstadoGuardado('error');
    } finally {
      setGuardando(null);
    }
  };

  if (!estado && cargando) {
    return <p className="text-sm text-[#5B6B64]">Cargando las bebidas...</p>;
  }
  // Sin el estado del modo (error al cargar) se deja al menos el bloque de
  // bebidas por menú, para no perder el acceso
  if (!estado) {
    return (
      <div className="flex flex-col gap-3" data-testid="bebidas-modo-sin-estado">
        <p className="text-xs text-[#8A1C12]">No se pudo cargar el modo de bebidas.</p>
        {children}
      </div>
    );
  }

  if (estado.aplica === false) {
    const hayBebidasAMano = !!estado.hay_bebidas_personalizadas || Number(estado.menus_con_bebida_incluida) > 0;
    return (
      <div className="flex flex-col gap-3" data-testid="bebidas-modo">
        <p className="text-sm text-[#5B6B64]" data-testid="bebidas-modo-no-aplica">
          {cubiertas > 0 ? (
            <span data-testid="bebidas-cubiertas-barra-libre">{textoCubiertas}</span>
          ) : hayBebidasAMano
            ? 'Los menús de este presupuesto no llevan el plato de refrescos; abajo, las bebidas elegidas a mano.'
            : (variado.length > 0
              ? 'Los menús de este presupuesto no llevan el plato de refrescos: no hay modo de bebidas que elegir. Estas son las bebidas que llevan.'
              : 'Los menús de este presupuesto no llevan bebida incluida; no hay modo de bebidas que elegir.')}
        </p>
        {/* Bebidas de los menús (copa de cava, botella de vino...) tal cual: las mismas de Compras y la hoja */}
        {(!hayBebidasAMano || cubiertas > 0) && variado.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" data-testid="bebidas-variado-tabla">
              <thead>
                <tr>
                  <th className={`${claseTh} text-left`}>Bebida</th>
                  <th className={`${claseTh} text-right`}>Unidades</th>
                  <th className={`${claseTh} text-left`}>Unidad</th>
                </tr>
              </thead>
              <tbody>
                {variado.map((b) => (
                  <tr key={b.ingrediente_id || b.nombre}>
                    <td className={`${claseTd} text-[#16221D]`}>{b.nombre}</td>
                    <td className={`${claseTd} text-right font-semibold tabular-nums`}>{b.unidades_enteras ?? Math.ceil(Number(b.unidades) || 0)}</td>
                    <td className={`${claseTd} text-[#5B6B64]`}>{b.unidad || 'ud'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {children}
      </div>
    );
  }

  const tonoCabecera = validado
    ? 'bg-[#E3F3EC] border-[#BFE3D2] text-[#0F5E43]'
    : 'bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]';
  const personalizadoSinConfirmar = modo === 'personalizado' && !estado?.bebidas_personalizadas_confirmadas;
  // El aviso del PUT solo tiene sentido mientras siga sin confirmar
  const avisoVisible = aviso && personalizadoSinConfirmar ? aviso : null;

  return (
    <div className="flex flex-col gap-4" data-testid="bebidas-modo">
      {cubiertas > 0 && (
        <p className="text-sm text-[#0F5E43]" data-testid="bebidas-cubiertas-barra-libre">{textoCubiertas}</p>
      )}
      {/* Estado de validación, arriba del todo como en Material */}
      <div
        className={`flex flex-col gap-0.5 rounded-[12px] border px-4 py-3.5 ${tonoCabecera}`}
        data-testid="bebidas-modo-validacion"
        data-estado={validado ? 'validado' : 'pendiente'}
      >
        {validado ? (
          <>
            <span className="text-base font-extrabold" data-testid="bebidas-modo-validado">
              Validado por {estado?.validado_por || 'alguien'}{formatFechaHora(estado?.validado_en) ? ` el ${formatFechaHora(estado.validado_en)}` : ''}
            </span>
            <span className="text-xs">{estado?.texto || 'Modo de bebidas elegido.'}</span>
          </>
        ) : (
          <>
            <span className="text-base font-extrabold">Pendiente de validar</span>
            <span className="text-xs">Elige el modo de bebidas del evento; al elegirlo queda validado.</span>
          </>
        )}
      </div>

      {/* Selector de modo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="radiogroup" aria-label="Modo de bebidas" data-testid="bebidas-modo-selector">
        {modos.map((m) => {
          const activo = m.valor === modo;
          const marcado = activo && validado;
          return (
            <button
              key={m.valor}
              type="button"
              role="radio"
              aria-checked={activo}
              onClick={() => elegirModo(m.valor)}
              disabled={!!guardando}
              className={`text-left rounded-[10px] border px-3 py-2.5 transition-colors disabled:cursor-wait ${
                activo
                  ? (marcado ? 'border-duetto-primary bg-[#E3F3EC]' : 'border-[#C9870F] bg-[#FFF8EC]')
                  : 'border-[#E1E6E3] bg-white hover:border-[#9FC7B6]'}`}
              title={activo && !validado ? 'Pulsa para validar este modo' : undefined}
              data-testid={`bebidas-modo-${m.valor}`}
            >
              <span className="flex items-center gap-2">
                <span
                  className={`w-4 h-4 rounded-full border-2 box-border flex items-center justify-center shrink-0 ${
                    activo ? (marcado ? 'border-duetto-primary' : 'border-[#C9870F]') : 'border-[#B7C1BC]'}`}
                >
                  {activo && <span className={`w-2 h-2 rounded-full ${marcado ? 'bg-duetto-primary' : 'bg-[#C9870F]'}`} />}
                </span>
                <span className={`text-sm font-bold ${activo ? 'text-[#16221D]' : 'text-[#33443C]'}`}>
                  {guardando === m.valor ? 'Guardando...' : (m.texto || m.valor)}
                </span>
              </span>
              <span className="block mt-1 pl-6 text-xs text-[#5B6B64]">{AYUDA_MODO[m.valor] || ''}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="text-xs text-[#8A1C12]" data-testid="bebidas-modo-error">{error}</p>}

      {modo === 'personalizado' ? (
        <div className="flex flex-col gap-3" data-testid="bebidas-modo-personalizado">
          {avisoVisible && (
            <div className="rounded-[10px] border px-3.5 py-3 text-sm bg-[#FDF0DC] border-[#F4D9AE] text-[#7A3A06]" data-testid="bebidas-modo-aviso">
              {avisoVisible}
            </div>
          )}
          {children}
        </div>
      ) : (
        <div className="flex flex-col gap-2" data-testid="bebidas-modo-variado">
          <span className="text-sm font-bold text-[#16221D]">Bebidas que salen en Compras y hoja de material</span>
          {variado.length === 0 ? (
            <p className="text-sm text-[#5B6B64]" data-testid="bebidas-variado-vacio">
              {cargando ? 'Calculando...' : 'Los menús de este presupuesto no llevan bebidas.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse" data-testid="bebidas-variado-tabla">
                <thead>
                  <tr>
                    <th className={`${claseTh} text-left`}>Bebida</th>
                    <th className={`${claseTh} text-right`}>Unidades</th>
                    <th className={`${claseTh} text-left`}>Unidad</th>
                  </tr>
                </thead>
                <tbody>
                  {variado.map((b) => (
                    <tr key={b.ingrediente_id || b.nombre}>
                      <td className={`${claseTd} text-[#16221D]`}>{b.nombre}</td>
                      <td className={`${claseTd} text-right font-semibold tabular-nums`}>{b.unidades_enteras ?? Math.ceil(Number(b.unidades) || 0)}</td>
                      <td className={`${claseTd} text-[#5B6B64]`}>{b.unidad || 'ud'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BebidasModo;
